import { useEffect, useRef, useState } from 'react';
import api from '../api/axios';
import { toast } from 'react-toastify';
import DoctorPatientInfo from './DoctorPatientInfo';
import './SendPrescriptionModal.css';

/* ── Modal scroll fix: keep body scrollable inside viewport ── */
const MODAL_BODY_STYLE = {
  overflowY: 'auto',
  maxHeight: 'calc(100vh - 200px)',
};

const emptyMedicine = () => ({ name: '', dose: '', frequency: '', days: '' });

const SIDEBAR_INPUT = {
  background: '#0A1227',
  border: '1px solid rgba(255,255,255,0.12)',
  color: '#fff',
  fontSize: '13px',
  colorScheme: 'dark',
  WebkitTextFillColor: '#fff',
  caretColor: '#fff',
};

function SendPrescriptionModal({ appointment, onClose, onSaved, asSidebar = false }) {
  const [medicines, setMedicines] = useState([emptyMedicine()]);
  const [generalAdvice, setGeneralAdvice] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState(null);

  // Digital signature states
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [existingSignature, setExistingSignature] = useState(null);
  const signatureInputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      if (!appointment?.appointment_id) return;
      try {
        const res = await api.get(
          `prescriptions/doctor/appointment/${appointment.appointment_id}/`
        );
        if (res.data.prescription) {
          const p = res.data.prescription;
          setExisting(p);
          setMedicines(
            p.medicines?.length ? p.medicines : [emptyMedicine()]
          );
          setGeneralAdvice(p.general_advice || '');
          if (p.doctor_signature) {
            setExistingSignature(p.doctor_signature);
          }
        }
      } catch (err) {
        console.error('Load prescription failed:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [appointment?.appointment_id]);

  const updateMedicine = (index, field, value) => {
    setMedicines((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  };

  const addRow = () => setMedicines((prev) => [...prev, emptyMedicine()]);

  const removeRow = (index) => {
    if (medicines.length <= 1) return;
    setMedicines((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSignatureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      toast.warning('Only PNG, JPG, or SVG images are allowed for signature');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.warning('Signature image must be under 2 MB');
      return;
    }
    setSignatureFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setSignaturePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeSignature = () => {
    setSignatureFile(null);
    setSignaturePreview(null);
    setExistingSignature(null);
    if (signatureInputRef.current) signatureInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valid = medicines.filter((m) => m.name?.trim());
    if (valid.length === 0) {
      toast.warning('Add at least one medicine name');
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('appointment_id', appointment.appointment_id);
      formData.append(
        'medicines',
        JSON.stringify(
          valid.map((m) => ({
            name: m.name.trim(),
            dose: m.dose?.trim() || '',
            frequency: m.frequency?.trim() || '',
            days: m.days?.trim() || '',
          }))
        )
      );
      formData.append('general_advice', generalAdvice.trim());

      if (signatureFile) {
        formData.append('doctor_signature', signatureFile);
      } else if (!existingSignature) {
        formData.append('remove_signature', 'true');
      }

      await api.post('prescriptions/doctor/send/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(existing ? 'Prescription updated' : 'Prescription sent to patient');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
        err.response?.data?.medicines?.[0] ||
        'Failed to send prescription'
      );
    } finally {
      setSaving(false);
    }
  };

  if (!appointment) return null;

  const activeSignature = signaturePreview || existingSignature;
  const title = existing ? 'Update Prescription' : 'Send Prescription';

  const formFields = (
    <>
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status" />
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6
              className={`fw-bold mb-0 ${asSidebar ? 'text-white' : ''}`}
              style={{ fontSize: asSidebar ? '14px' : undefined }}
            >
              Medicines
            </h6>
            {existing && asSidebar && (
              <span
                className="badge bg-success bg-opacity-20 text-white border border-success border-opacity-25 px-2 py-1 small rounded-2"
                style={{ fontSize: '10px' }}
              >
                Saved
              </span>
            )}
          </div>

          {medicines.map((med, index) => (
            <div
              key={index}
              className={
                asSidebar
                  ? 'p-3 rounded-4 mb-2 border border-secondary border-opacity-25'
                  : 'row g-2 mb-2 align-items-end'
              }
              style={asSidebar ? { background: '#121D38' } : undefined}
            >
              {asSidebar ? (
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex align-items-center justify-content-between gap-2">
                    <small className="text-white-50" style={{ fontSize: '11px' }}>
                      Medicine {index + 1}
                    </small>
                    <button
                      type="button"
                      className="btn btn-sm p-0 text-danger"
                      onClick={() => removeRow(index)}
                      disabled={medicines.length <= 1}
                      title="Remove row"
                      style={{ lineHeight: 1 }}
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                  <input
                    type="text"
                    className="form-control form-control-sm rx-sidebar-input"
                    placeholder="Medicine name *"
                    value={med.name}
                    onChange={(e) => updateMedicine(index, 'name', e.target.value)}
                    required={index === 0}
                    style={SIDEBAR_INPUT}
                  />
                  <div className="row g-2">
                    <div className="col-4">
                      <input
                        type="text"
                        className="form-control form-control-sm rx-sidebar-input"
                        placeholder="Dose"
                        value={med.dose}
                        onChange={(e) => updateMedicine(index, 'dose', e.target.value)}
                        style={SIDEBAR_INPUT}
                      />
                    </div>
                    <div className="col-5">
                      <input
                        type="text"
                        className="form-control form-control-sm rx-sidebar-input"
                        placeholder="Frequency"
                        value={med.frequency}
                        onChange={(e) => updateMedicine(index, 'frequency', e.target.value)}
                        style={SIDEBAR_INPUT}
                      />
                    </div>
                    <div className="col-3">
                      <input
                        type="text"
                        className="form-control form-control-sm rx-sidebar-input"
                        placeholder="Days"
                        value={med.days}
                        onChange={(e) => updateMedicine(index, 'days', e.target.value)}
                        style={SIDEBAR_INPUT}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Medicine name *"
                      value={med.name}
                      onChange={(e) => updateMedicine(index, 'name', e.target.value)}
                      required={index === 0}
                    />
                  </div>
                  <div className="col-md-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Dose"
                      value={med.dose}
                      onChange={(e) => updateMedicine(index, 'dose', e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Frequency"
                      value={med.frequency}
                      onChange={(e) => updateMedicine(index, 'frequency', e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Days"
                      value={med.days}
                      onChange={(e) => updateMedicine(index, 'days', e.target.value)}
                    />
                  </div>
                  <div className="col-md-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeRow(index)}
                      disabled={medicines.length <= 1}
                      title="Remove row"
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}

          <button
            type="button"
            className={`btn btn-sm mb-3 ${asSidebar ? 'btn-outline-primary w-100 py-2 rounded-3 fw-semibold' : 'btn-outline-primary'}`}
            onClick={addRow}
            style={
              asSidebar
                ? { color: '#60a5fa', borderColor: '#2563eb', fontSize: '13px' }
                : undefined
            }
          >
            <i className="bi bi-plus-lg me-1" /> Add medicine
          </button>

          <label
            className={`form-label fw-bold ${asSidebar ? 'text-white' : ''}`}
            style={{ fontSize: asSidebar ? '14px' : undefined }}
          >
            General advice
          </label>
          <textarea
            className={`form-control mb-3 ${asSidebar ? 'rounded-4 rx-sidebar-input' : ''}`}
            rows={3}
            placeholder="Rest, diet, follow-up, etc."
            value={generalAdvice}
            onChange={(e) => setGeneralAdvice(e.target.value)}
            style={asSidebar ? { ...SIDEBAR_INPUT, background: '#121D38' } : undefined}
          />

          {/* Digital Signature */}
          <div
            className={
              asSidebar
                ? 'p-3 rounded-4 mb-2 border border-secondary border-opacity-25'
                : 'border rounded-3 p-3 mb-2'
            }
            style={asSidebar ? { background: '#121D38' } : { background: '#f8f9ff' }}
          >
            <label
              className={`form-label fw-bold mb-2 d-flex align-items-center gap-2 ${asSidebar ? 'text-white' : ''}`}
              style={{ fontSize: asSidebar ? '14px' : undefined }}
            >
              <i className="bi bi-pen text-primary" />
              Digital Signature
            </label>

            {activeSignature ? (
              <div className="d-flex align-items-start gap-3 flex-wrap">
                <div
                  className="border rounded-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: asSidebar ? '100%' : 220,
                    height: 90,
                    background: asSidebar ? '#0A1227' : '#fff',
                    borderColor: asSidebar ? 'rgba(255,255,255,0.12)' : undefined,
                    overflow: 'hidden',
                    boxShadow: asSidebar ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
                  }}
                >
                  <img
                    src={activeSignature}
                    alt="Doctor signature preview"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </div>
                <div className={`d-flex ${asSidebar ? 'w-100 flex-row' : 'flex-column'} gap-2`}>
                  {!asSidebar && (
                    <span className="text-success small fw-semibold">
                      <i className="bi bi-check-circle me-1" />
                      Signature uploaded
                    </span>
                  )}
                  <button
                    type="button"
                    className={`btn btn-sm ${asSidebar ? 'btn-outline-primary flex-grow-1 rounded-3' : 'btn-outline-primary'}`}
                    onClick={() => signatureInputRef.current?.click()}
                    style={asSidebar ? { color: '#60a5fa', borderColor: '#2563eb', fontSize: '12px' } : undefined}
                  >
                    <i className="bi bi-arrow-repeat me-1" />
                    Change
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${asSidebar ? 'btn-outline-danger flex-grow-1 rounded-3' : 'btn-outline-danger'}`}
                    onClick={removeSignature}
                    style={asSidebar ? { fontSize: '12px' } : undefined}
                  >
                    <i className="bi bi-x-circle me-1" />
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="d-flex flex-column align-items-center justify-content-center border border-dashed rounded-3 p-4 text-center"
                style={{
                  cursor: 'pointer',
                  minHeight: 110,
                  borderStyle: 'dashed',
                  borderColor: asSidebar ? 'rgba(96,165,250,0.45)' : '#6c86c8',
                  background: asSidebar ? '#0A1227' : '#eef1ff',
                  transition: 'background 0.2s',
                }}
                onClick={() => signatureInputRef.current?.click()}
              >
                <i className="bi bi-upload fs-3 text-primary mb-2" />
                <p className={`mb-0 fw-semibold ${asSidebar ? 'text-white' : 'text-primary'}`}>
                  Click to upload signature
                </p>
                <p className={`mb-0 small ${asSidebar ? 'text-white-50' : 'text-muted'}`}>
                  PNG, JPG or SVG · max 2 MB
                </p>
              </div>
            )}

            <input
              ref={signatureInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/svg+xml"
              className="d-none"
              onChange={handleSignatureChange}
            />

            <p className={`small mt-2 mb-0 ${asSidebar ? 'text-white-50' : 'text-muted'}`}>
              <i className="bi bi-info-circle me-1" />
              Your signature will appear on the prescription PDF/print.
            </p>
          </div>
        </>
      )}
    </>
  );

  /* ── Right sidebar panel (VideoCall) — matches Booking Info & Report ── */
  if (asSidebar) {
    return (
      <div
        className="rx-sidebar-panel"
        style={{
          width: '380px',
          height: '100%',
          backgroundColor: '#0A1227',
          borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          overflowY: 'auto',
          zIndex: 90,
          position: 'relative',
        }}
      >
        <div className="d-flex justify-content-between align-items-center border-bottom border-secondary border-opacity-25 pb-3">
          <div className="d-flex align-items-center">
            <i className="bi bi-file-medical-fill text-success fs-4 me-2" />
            <h5 className="fw-bold mb-0 text-white" style={{ fontSize: '17px' }}>
              {title}
            </h5>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-link text-white-50 p-0 text-decoration-none"
            onClick={onClose}
          >
            <i className="bi bi-x-lg fs-5" />
          </button>
        </div>

        {/* Patient card */}
        <div
          className="p-3 rounded-4 border border-secondary border-opacity-25 d-flex align-items-start gap-3"
          style={{ background: '#121D38' }}
        >
          <div
            className="bg-primary bg-opacity-10 text-primary rounded-3 p-2 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: '42px', height: '42px' }}
          >
            <i className="bi bi-person-fill fs-4 text-white" />
          </div>
          <div className="overflow-hidden">
            <small className="text-white-50 d-block" style={{ fontSize: '11px' }}>
              Patient
            </small>
            <DoctorPatientInfo
              appointment={appointment}
              titleClass="fw-bold text-white mb-1"
              subtitleClass="text-white-50 small mb-0"
            />
          </div>
        </div>

        {/* Visit card */}
        <div
          className="p-3 rounded-4 border border-secondary border-opacity-25 d-flex align-items-start gap-3"
          style={{ background: '#121D38' }}
        >
          <div
            className="bg-primary bg-opacity-10 text-primary rounded-3 p-2 d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: '42px', height: '42px' }}
          >
            <i className="bi bi-calendar-event-fill fs-5 text-white" />
          </div>
          <div>
            <small className="text-white-50 d-block" style={{ fontSize: '11px' }}>
              Visit
            </small>
            <h6 className="fw-bold text-white mb-1" style={{ fontSize: '14px' }}>
              {appointment.date} · {appointment.start_time}
            </h6>
            <small className="text-white-50 d-block" style={{ fontSize: '12px' }}>
              Write & send prescription to patient
            </small>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
          <div className="flex-grow-1">{formFields}</div>

          <div className="d-flex flex-column gap-2 mt-2 pt-2 border-top border-secondary border-opacity-25">
            <button
              type="submit"
              className="btn btn-success w-100 py-2 rounded-3 fw-semibold"
              disabled={loading || saving}
              style={{ fontSize: '13px' }}
            >
              {saving ? 'Saving…' : existing ? 'Update & notify patient' : 'Send to patient'}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary w-100 py-2 rounded-3 fw-semibold border-secondary border-opacity-50 text-white-50"
              onClick={onClose}
              style={{ fontSize: '13px' }}
            >
              Cancel
            </button>
          </div>

          <div className="p-3 rounded-4 bg-primary bg-opacity-10 border border-primary border-opacity-25 d-flex align-items-start gap-2 mt-3">
            <i className="bi bi-info-circle-fill text-primary fs-5 flex-shrink-0 mt-1" />
            <span className="small text-white" style={{ fontSize: '11px', lineHeight: '1.4' }}>
              Patient will be notified when you send or update this prescription.
            </span>
          </div>
        </form>
      </div>
    );
  }

  /* ── Classic centered modal (Doctor Appointments, etc.) ── */
  return (
    <div
      className="modal d-flex align-items-center justify-content-center"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', inset: 0, zIndex: 1050, overflowY: 'auto' }}
    >
      <div className="modal-dialog modal-lg w-100 my-3" style={{ maxWidth: 800 }}>
        <div className="modal-content border-0 shadow-lg rounded-4">
          <div className="modal-header border-0">
            <h5 className="modal-title fw-bold">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={MODAL_BODY_STYLE}>
              <div className="mb-3 p-3 bg-light rounded-3">
                <DoctorPatientInfo appointment={appointment} />
                <p className="text-muted small mb-0 mt-2">
                  <i className="bi bi-calendar3 me-1" />
                  Visit: {appointment.date} · {appointment.start_time}
                </p>
              </div>
              {formFields}
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary rounded-pill" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary rounded-pill px-4"
                disabled={loading || saving}
              >
                {saving ? 'Saving…' : existing ? 'Update & notify patient' : 'Send to patient'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SendPrescriptionModal;
